import React, { useState } from "react";
import { Link } from "wouter";
import { Layout } from "@/components/layout";
import {
  useListMyEnrollments,
  useListMySnippets,
  useListMyMessages,
  useListMyDirectMessages,
  getListMySnippetsQueryKey,
  getListMyEnrollmentsQueryKey,
  getListMyMessagesQueryKey,
  getListMyDirectMessagesQueryKey,
  deleteSnippet,
  updateSnippet,
} from "@workspace/api-client-react";
import type { CodeSnippetWithLesson } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  BookOpen,
  Clock,
  LogIn,
  Code,
  Share2,
  Trash2,
  ExternalLink,
  Check,
  Copy,
  Pencil,
  X,
  Mail,
  Calendar as CalendarIcon,
  Download,
  Radio,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { dateShortFmt, dateLongFmt } from "@/lib/format";
import {
  enrollmentIcsUrl,
  formatLocalSessionTime,
  googleCalendarUrl,
  isJoinable,
} from "@/lib/calendar";
import { Show, useUser } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { basePath } from "@/lib/clerk";

function SignedOutPanel() {
  return (
    <div className="container mx-auto px-4 py-16 max-w-3xl">
      <div className="bg-card border border-border rounded-2xl p-10 md:p-14 text-center shadow-sm">
        <div className="w-14 h-14 mx-auto mb-6 bg-primary/10 text-primary rounded-full flex items-center justify-center">
          <LogIn className="w-7 h-7" aria-hidden="true" />
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4 text-balance">
          Sign in to see your <span className="font-italic-serif text-primary">classes.</span>
        </h2>
        <p className="text-muted-foreground max-w-md mx-auto mb-8">
          Your enrolled courses, lesson materials, and live sessions all live behind your MainLoop account.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild size="lg" className="h-12 rounded-full px-8 font-bold">
            <Link href="/sign-in">Sign in</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="h-12 rounded-full px-8 font-bold">
            <Link href="/sign-up">Create an account</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function SnippetCard({
  s,
  onDelete,
  onRename,
}: {
  s: {
    id: string;
    title: string;
    language: "python" | "web";
    updatedAt: string;
    lessonId: number | null;
    lesson: { id: number; courseTitle: string; orderIndex: number } | null;
  };
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState(s.title);
  const [savingRename, setSavingRename] = useState(false);
  const url = `${window.location.origin}${basePath}/s/${s.id}`;
  const target = s.lesson ? `/lessons/${s.lesson.id}` : `/s/${s.id}`;

  const startRename = () => {
    setDraftTitle(s.title);
    setRenaming(true);
  };
  const submitRename = async () => {
    const next = draftTitle.trim();
    if (!next || next === s.title) {
      setRenaming(false);
      return;
    }
    setSavingRename(true);
    try {
      await onRename(s.id, next);
      setRenaming(false);
    } finally {
      setSavingRename(false);
    }
  };

  return (
    <div className="group p-5 rounded-xl border border-border bg-card hover:border-primary transition-colors">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <Code className="w-3.5 h-3.5" />
          {s.language === "python" ? "python" : "web"}
        </div>
        <span className="text-[10px] text-muted-foreground">
          {dateShortFmt.format(new Date(s.updatedAt))}
        </span>
      </div>
      {renaming ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitRename();
          }}
          className="flex items-center gap-2 mb-1"
        >
          <Input
            autoFocus
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                setRenaming(false);
              }
            }}
            disabled={savingRename}
            maxLength={200}
            aria-label="Snippet title"
            className="h-8 text-sm font-display font-bold"
          />
          <Button
            type="submit"
            size="sm"
            variant="default"
            className="h-8 px-2"
            disabled={savingRename || !draftTitle.trim()}
            aria-label="Save title"
          >
            <Check className="w-3.5 h-3.5" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 px-2"
            disabled={savingRename}
            onClick={() => setRenaming(false)}
            aria-label="Cancel rename"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </form>
      ) : (
        <Link href={target} className="block">
          <div className="font-display font-bold text-base group-hover:text-primary transition-colors mb-1">
            {s.title}
          </div>
        </Link>
      )}
      <Link href={target} className="block">
        {s.lesson ? (
          <div className="text-xs text-muted-foreground">
            From {s.lesson.courseTitle} · Lesson{" "}
            {String(s.lesson.orderIndex).padStart(2, "0")}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground italic">
            Standalone snippet
          </div>
        )}
        <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
          <Share2 className="w-3 h-3" /> /s/{s.id}
        </div>
      </Link>
      <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-border">
        <Button asChild size="sm" variant="outline" className="h-8 text-xs">
          <Link href={target}>
            Open <ExternalLink className="w-3 h-3 ml-1.5" />
          </Link>
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url);
              setCopied(true);
              setTimeout(() => setCopied(false), 1800);
            } catch {
              // ignore
            }
          }}
        >
          {copied ? (
            <Check className="w-3 h-3 mr-1.5" />
          ) : (
            <Copy className="w-3 h-3 mr-1.5" />
          )}
          {copied ? "Copied" : "Share"}
        </Button>
        <div className="flex-1" />
        {!renaming && !confirming && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 text-xs text-muted-foreground hover:text-primary"
            onClick={startRename}
            aria-label={`Rename snippet ${s.title}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        )}
        {confirming ? (
          <>
            <span className="text-xs text-muted-foreground">Sure?</span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className="h-8 text-xs"
              onClick={() => {
                setConfirming(false);
                onDelete(s.id);
              }}
            >
              Delete
            </Button>
          </>
        ) : (
          !renaming && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-muted-foreground hover:text-destructive"
              onClick={() => setConfirming(true)}
              aria-label={`Delete snippet ${s.title}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )
        )}
      </div>
    </div>
  );
}

function LiveSessionsPanel({
  enrollmentId,
  courseTitle,
  sessions,
}: {
  enrollmentId: number;
  courseTitle: string;
  sessions: Array<{
    lessonId: number;
    lessonTitle: string;
    lessonOrderIndex: number;
    zoomScheduledAt: string;
    zoomUrl: string | null;
    zoomLiveStartedAt: string | null;
  }>;
}) {
  // Re-render every 30s so the per-session Join button enables itself the
  // moment the time-window opens, without the student needing to refresh.
  const [, force] = useState(0);
  React.useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const next = sessions[0];
  const upcoming = sessions.slice(1, 3);

  return (
    <div className="px-6 pb-4 border-t pt-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          // next live sessions
        </div>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="h-7 text-[11px] rounded-full"
        >
          <a
            href={enrollmentIcsUrl(enrollmentId)}
            download
            aria-label={`Download ${courseTitle} calendar invite`}
          >
            <Download className="w-3 h-3 mr-1.5" /> .ics
          </a>
        </Button>
      </div>

      {next ? (
        <NextSessionRow session={next} courseTitle={courseTitle} primary />
      ) : (
        <p className="text-xs text-muted-foreground italic">
          No upcoming sessions scheduled. The .ics will fill in once your
          teacher publishes the schedule.
        </p>
      )}

      {upcoming.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {upcoming.map((s) => (
            <li key={s.lessonId}>
              <NextSessionRow session={s} courseTitle={courseTitle} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NextSessionRow({
  session,
  courseTitle,
  primary,
}: {
  session: {
    lessonId: number;
    lessonTitle: string;
    lessonOrderIndex: number;
    zoomScheduledAt: string;
    zoomUrl: string | null;
    zoomLiveStartedAt: string | null;
  };
  courseTitle: string;
  primary?: boolean;
}) {
  const joinable = isJoinable(session);
  const teacherStarted = !!session.zoomLiveStartedAt;
  const label = `Lesson ${String(session.lessonOrderIndex).padStart(2, "0")} · ${session.lessonTitle}`;
  const gcal = googleCalendarUrl({
    title: `${courseTitle} — ${label}`,
    start: new Date(session.zoomScheduledAt),
    location: session.zoomUrl ?? undefined,
  });

  return (
    <div
      className={
        primary
          ? "rounded-lg border bg-muted/30 p-3"
          : "flex items-center justify-between gap-2 text-xs text-muted-foreground"
      }
    >
      <div className="min-w-0 flex-1">
        <div className={primary ? "font-display font-bold text-sm leading-tight truncate" : "truncate"}>
          {label}
        </div>
        <div className={primary ? "text-xs text-muted-foreground mt-0.5" : "text-[11px] text-muted-foreground/80"}>
          {formatLocalSessionTime(session.zoomScheduledAt)}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-[11px]">
          <a
            href={gcal}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Add ${label} to Google Calendar`}
            title="Add to Google Calendar"
          >
            <CalendarIcon className="w-3 h-3" />
          </a>
        </Button>
        {joinable && session.zoomUrl ? (
          <Button asChild size="sm" className="h-7 px-3 text-[11px]">
            <a href={session.zoomUrl} target="_blank" rel="noopener noreferrer">
              <Radio className="w-3 h-3 mr-1" />
              {teacherStarted ? "Join live" : "Join"}
            </a>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function SignedInClasses() {
  const { user, isLoaded } = useUser();
  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: enrollments, isLoading, isError } = useListMyEnrollments({
    query: {
      enabled: !!email,
      queryKey: getListMyEnrollmentsQueryKey(),
    },
  });
  const { data: snippets } = useListMySnippets();
  const { data: messages } = useListMyMessages({
    query: {
      enabled: !!email,
      queryKey: getListMyMessagesQueryKey(),
    },
  });
  const { data: directMessages } = useListMyDirectMessages({
    query: {
      enabled: !!email,
      queryKey: getListMyDirectMessagesQueryKey(),
    },
  });

  const handleDelete = async (id: string) => {
    try {
      await deleteSnippet(id);
      qc.invalidateQueries({ queryKey: getListMySnippetsQueryKey() });
      toast({ title: "Deleted", description: "Snippet removed." });
    } catch (e) {
      toast({
        title: "Couldn't delete",
        description: e instanceof Error ? e.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  const handleRename = async (id: string, title: string) => {
    try {
      await updateSnippet(id, { title });
      qc.invalidateQueries({ queryKey: getListMySnippetsQueryKey() });
      toast({ title: "Renamed", description: "Snippet title updated." });
    } catch (e) {
      toast({
        title: "Couldn't rename",
        description: e instanceof Error ? e.message : "Try again.",
        variant: "destructive",
      });
      throw e;
    }
  };

  if (!isLoaded) {
    return (
      <div className="container mx-auto px-4 py-24 text-center text-muted-foreground" role="status">
        Loading your account&hellip;
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 md:py-16 max-w-5xl">
      <div className="mb-10 pb-6 border-b border-border flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
            // signed in as
          </div>
          <div className="font-display text-2xl font-bold tracking-tight">{email}</div>
        </div>
        <Button asChild variant="outline" className="rounded-full">
          <Link href="/courses">Browse catalog →</Link>
        </Button>
      </div>

      {isLoading && (
        <div className="py-16 text-center text-muted-foreground" role="status">
          Loading your classes&hellip;
        </div>
      )}

      {isError && (
        <div className="bg-destructive/10 text-destructive border border-destructive/20 rounded-xl p-6 text-center" role="alert">
          <p className="font-medium">Couldn&rsquo;t load your enrollments. Please try again in a moment.</p>
        </div>
      )}

      {!isLoading && !isError && enrollments && (
        <div aria-live="polite">
          {enrollments.length === 0 ? (
            <div className="bg-muted/40 rounded-2xl p-12 text-center border border-dashed border-border">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-60" aria-hidden="true" />
              <h3 className="font-display text-2xl font-bold mb-2">No enrollments yet</h3>
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                You haven&rsquo;t enrolled in any courses with this email. Find one that fits.
              </p>
              <Button asChild className="rounded-full">
                <Link href="/courses">Browse Catalog</Link>
              </Button>
            </div>
          ) : (
            <>
              {(() => {
                const active = enrollments.filter((e) => e.status === "active");
                const refunded = enrollments.filter((e) => e.status === "refunded");
                return (
                  <>
                    {active.length === 0 && refunded.length > 0 && (
                      <div className="bg-muted/40 rounded-2xl p-8 text-center border border-dashed border-border mb-8">
                        <p className="text-muted-foreground mb-4">
                          You don&rsquo;t have any current courses. Browse the catalog to enroll again.
                        </p>
                        <Button asChild className="rounded-full">
                          <Link href="/courses">Browse Catalog</Link>
                        </Button>
                      </div>
                    )}
                    {active.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {active.map((enrollment) => (
                          <Card key={enrollment.id} className="flex flex-col hover:shadow-md transition-shadow overflow-hidden group">
                            <div className="h-2 bg-gradient-to-r from-primary to-secondary w-full" />
                            <CardHeader className="pb-2">
                              <div className="flex justify-between items-start mb-2">
                                <Badge variant={enrollment.course.format === "live" ? "default" : "secondary"} className="uppercase text-[10px] tracking-wider">
                                  {enrollment.course.format === "live" ? "Live Cohort" : "Self-Paced"}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  Enrolled {dateShortFmt.format(new Date(enrollment.createdAt))}
                                </span>
                              </div>
                              <h3 className="font-display text-xl font-bold tracking-tight group-hover:text-primary transition-colors">
                                {enrollment.course.title}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                Student: {enrollment.studentName} ({enrollment.gradeLevel})
                              </p>
                            </CardHeader>
                            <CardContent className="flex-1 pb-4">
                              <div className="flex items-center gap-4 text-sm font-medium">
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <BookOpen className="w-4 h-4" /> {enrollment.course.lessonCount} Lessons
                                </div>
                                {enrollment.course.format === "live" && enrollment.course.durationWeeks && (
                                  <div className="flex items-center gap-1 text-muted-foreground">
                                    <Clock className="w-4 h-4" /> {enrollment.course.durationWeeks} Weeks
                                  </div>
                                )}
                              </div>
                            </CardContent>
                            {enrollment.course.format === "live" && (
                              <LiveSessionsPanel
                                enrollmentId={enrollment.id}
                                courseTitle={enrollment.course.title}
                                sessions={enrollment.upcomingSessions}
                              />
                            )}
                            <CardFooter className="bg-muted/30 border-t pt-4">
                              <Button asChild className="w-full justify-between" variant="ghost">
                                <Link href={`/learn/${enrollment.course.slug}`}>
                                  Go to Course <ArrowRight className="w-4 h-4" />
                                </Link>
                              </Button>
                            </CardFooter>
                          </Card>
                        ))}
                      </div>
                    )}

                    {refunded.length > 0 && (
                      <div className="mt-12">
                        <div className="mb-4 pb-3 border-b border-border">
                          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">
                            // past / refunded
                          </div>
                          <h2 className="font-display text-xl font-bold tracking-tight">
                            Refunded courses
                          </h2>
                          <p className="text-xs text-muted-foreground mt-1">
                            Access was revoked when Stripe processed the refund. You can re-enroll anytime from the course page.
                          </p>
                        </div>
                        <ul className="space-y-2">
                          {refunded.map((enrollment) => (
                            <li
                              key={enrollment.id}
                              className="border border-border rounded-xl px-5 py-4 bg-muted/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                            >
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="font-display font-bold text-base text-muted-foreground line-through">
                                    {enrollment.course.title}
                                  </div>
                                  <span className="inline-flex items-center rounded-full border border-destructive/30 bg-destructive/10 text-destructive px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest">
                                    Refunded
                                  </span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  Enrolled {dateShortFmt.format(new Date(enrollment.createdAt))}
                                  {enrollment.refundedAt
                                    ? ` · refunded ${dateShortFmt.format(new Date(enrollment.refundedAt))}`
                                    : ""}
                                </div>
                              </div>
                              <Button asChild size="sm" variant="outline" className="rounded-full shrink-0">
                                <Link href={`/courses/${enrollment.course.slug}`}>
                                  Re-enroll
                                </Link>
                              </Button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      )}

      {messages && messages.length > 0 && (
        <div className="mt-16">
          <div className="mb-6 pb-4 border-b border-border flex items-end justify-between">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
                // from your teachers
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-2">
                <Mail className="w-5 h-5" aria-hidden="true" /> Messages
              </h2>
            </div>
            <Badge variant="outline" className="rounded-full">
              {messages.length} {messages.length === 1 ? "message" : "messages"}
            </Badge>
          </div>
          <ul className="space-y-3">
            {messages.map((m) => (
              <li key={m.id} className="border border-border rounded-xl p-5 bg-card">
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-bold leading-tight">{m.subject}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {m.teacherName} · {m.course.title}
                    </div>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
                    {dateLongFmt.format(new Date(m.createdAt))}
                  </div>
                </div>
                <p className="text-sm text-foreground/90 whitespace-pre-wrap mt-2">{m.body}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {directMessages && directMessages.length > 0 && (
        <div className="mt-16">
          <div className="mb-6 pb-4 border-b border-border flex items-end justify-between">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
                // direct from your teacher
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-2">
                <Mail className="w-5 h-5" aria-hidden="true" /> Direct messages
              </h2>
            </div>
            <Badge variant="outline" className="rounded-full">
              {directMessages.length} {directMessages.length === 1 ? "message" : "messages"}
            </Badge>
          </div>
          <ul className="space-y-3">
            {directMessages.map((m) => (
              <li key={m.id} className="border border-border rounded-xl p-5 bg-card">
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <div className="min-w-0">
                    <div className="font-display text-base font-bold leading-tight">
                      From {m.fromTeacherName}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {m.course.title}
                    </div>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
                    {dateLongFmt.format(new Date(m.createdAt))}
                  </div>
                </div>
                <p className="text-sm text-foreground/90 whitespace-pre-wrap mt-2">{m.body}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {snippets && snippets.length > 0 && (
        <div className="mt-16">
          <div className="mb-6 pb-4 border-b border-border flex items-end justify-between">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
                // saved snippets
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight">
                Your code
              </h2>
            </div>
          </div>

          <SnippetsByLesson
            snippets={snippets}
            onDelete={handleDelete}
            onRename={handleRename}
          />
        </div>
      )}
    </div>
  );
}

type SnippetItem = CodeSnippetWithLesson;

function SnippetsByLesson({
  snippets,
  onDelete,
  onRename,
}: {
  snippets: SnippetItem[];
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => Promise<void>;
}) {
  // Group by lesson; standalone (lessonId == null) snippets land in their own
  // bucket at the bottom.
  const groups = new Map<
    string,
    {
      key: string;
      label: string;
      sublabel: string | null;
      lessonId: number | null;
      items: SnippetItem[];
    }
  >();
  for (const s of snippets) {
    const key = s.lesson ? `lesson-${s.lesson.id}` : "standalone";
    let group = groups.get(key);
    if (!group) {
      group = s.lesson
        ? {
            key,
            label: `Lesson ${String(s.lesson.orderIndex).padStart(2, "0")} · ${s.lesson.title ?? ""}`.trim(),
            sublabel: s.lesson.courseTitle,
            lessonId: s.lesson.id,
            items: [],
          }
        : {
            key,
            label: "Standalone snippets",
            sublabel: null,
            lessonId: null,
            items: [],
          };
      groups.set(key, group);
    }
    group.items.push(s);
  }
  const ordered = [...groups.values()].sort((a, b) => {
    if (a.key === "standalone") return 1;
    if (b.key === "standalone") return -1;
    return (a.lessonId ?? 0) - (b.lessonId ?? 0);
  });

  return (
    <div className="space-y-10">
      {ordered.map((group) => (
        <div key={group.key}>
          <div className="flex items-end justify-between gap-4 mb-3">
            <div className="min-w-0">
              {group.sublabel && (
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1 truncate">
                  {group.sublabel}
                </div>
              )}
              <h3 className="font-display text-lg font-bold tracking-tight truncate">
                {group.label}
              </h3>
            </div>
            {group.lessonId != null && (
              <Button
                asChild
                size="sm"
                variant="ghost"
                className="h-8 text-xs whitespace-nowrap"
              >
                <Link href={`/lessons/${group.lessonId}`}>
                  Open lesson <ArrowRight className="w-3 h-3 ml-1.5" />
                </Link>
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {group.items.map((s) => (
              <SnippetCard
                key={s.id}
                s={s}
                onDelete={onDelete}
                onRename={onRename}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MyClasses() {
  return (
    <Layout>
      <div className="border-b border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-50" aria-hidden="true" />
        <div className="container mx-auto px-4 py-20 md:py-24 relative z-10 max-w-3xl">
          <div className="section-tag mb-4">[ STUDENT PORTAL ] · my classes</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight mb-6 text-balance">
            Welcome <span className="font-italic-serif text-primary">back.</span>
          </h1>
          <p className="text-xl text-muted-foreground">
            Pick up where you left off — your courses, lessons, and live sessions.
          </p>
        </div>
      </div>

      <Show when="signed-out">
        <SignedOutPanel />
      </Show>
      <Show when="signed-in">
        <SignedInClasses />
      </Show>
    </Layout>
  );
}
